// SCR-STAFF-302 — دیالوگ رزرو (FLOW-02، بخش‌های فاز 0؛ US-SCH-010، 020، 021، 030): مراجعه‌کننده، کانال تلفن/حضوری، خدمت، تعداد،
// ارائه‌دهنده یا «هر کدام»، نوار روزها، «اولین زمان آزاد ⚡»، زمان‌های آزاد گروه‌بندی‌شده به تفکیک ارائه‌دهنده (کلیدهای جهت بین
// زمان‌ها، Enter انتخاب)، خلاصه با قیمت (ITariffQuery)، هشدارها، یادداشت و Ctrl+Enter ثبت. رقابت همزمان (PHX-SCH-0201) ← نتایج
// تازه و انتخاب زمان بعدی. همان دیالوگ برای جابه‌جایی (بدون تغییر مراجعه‌کننده و خدمت) و مراجعه بدون نوبت (اولین زمان آزاد امروز).
// AP-SCH-02: نگه‌داشت ۱۰ دقیقه‌ای زمان انتخاب‌شده (US-SCH-014)، بیعانه سیاست و معافیت با Override (US-SCH-023)، تغییر دستی اتاق میان
// اتاق‌های آزاد (AC-SCH-011.2)، نوبت تکرارشونده با پیش‌نمایش تداخل و جایگزین (US-SCH-022)، جابه‌جایی «همین/همه بعدی» سری و ثبت سریع در
// لیست انتظار وقتی زمان آزادی نیست (AC-SCH-050.2).
import BoltIcon from '@mui/icons-material/Bolt';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  isApiError,
  unwrap,
  type AppointmentChannel,
  type BookingPreviewView,
  type BookingServiceOption,
  type HoldView,
  type SeriesPreviewView,
  type PatientSearchItem,
  type SlotSearchView,
  type SlotView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, latinDigits, Money } from '@phoenix/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type Ref } from 'react';
import { useDebounced } from '../patients/common';
import { useLockPolicy } from './appointmentLock';
import { addDays, BranchRequired, formatDay, formatTime, PriceText, schKeys, useActiveBranch, useBookingOptions, useServiceSearch } from './common';

export const SCR_STAFF_302 = 'SCR-STAFF-302';

export type BookingMode = 'book' | 'reschedule' | 'walk-in';

export interface BookingPatient {
  id: string;
  displayName: string;
  fileNumber?: string | null;
}

export interface BookingPrefill {
  patient?: BookingPatient | null;
  serviceId?: string | null;
  quantity?: number;
  providerId?: string | null;
  roomId?: string | null;
  /** تاریخ کسب‌وکاری yyyy-MM-dd. */
  date?: string | null;
  /** شروع پیشنهادی (ISO). */
  start?: string | null;
  /** جابه‌جایی: نوبت و نسخه. */
  appointmentId?: string;
  rowVersion?: number | string;
  /** جابه‌جایی جلسه سری: «همین» یا «همه بعدی» (AC-SCH-022.2). */
  seriesId?: string | null;
  /** وضعیت نوبت؛ برای قفل جابه‌جایی. */
  status?: string | null;
}

export interface BookingResult {
  appointmentId: string;
  mode: BookingMode;
  patientName: string;
}

export interface BookingDialogProps {
  open: boolean;
  mode: BookingMode;
  prefill?: BookingPrefill;
  onClose: () => void;
  onDone: (result: BookingResult) => void;
}

const STRIP_DAYS = 7;

/** کلید پیش‌نمایش بدون بخش پایانی «استفاده از پکیج». */
function withoutPackageFlag(key: string): string {
  return key.slice(0, key.lastIndexOf('|'));
}

function slotKey(slot: Pick<SlotView, 'providerId' | 'start'>): string {
  return `${slot.providerId}|${new Date(slot.start).toISOString()}`;
}

/** جستجوی زنده مراجعه‌کننده (نام، موبایل، شماره پرونده — US-PAT-010). */
export function PatientPicker({
  value,
  onChange,
  disabled,
  inputRef,
}: {
  value: BookingPatient | null;
  onChange: (value: BookingPatient | null) => void;
  disabled: boolean;
  inputRef: Ref<HTMLInputElement>;
}) {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const [input, setInput] = useState('');
  const q = useDebounced(latinDigits(input).trim(), 200);
  const label = (option: BookingPatient) =>
    option.fileNumber ? `${option.displayName} · ${faDigits(option.fileNumber)}` : option.displayName;
  // مراجعه‌کننده پیش‌انتخاب (ادامه ثبت سریع، جابه‌جایی) متن ورودی را هم پر می‌کند.
  useEffect(() => {
    if (value) {
      setInput(label(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.id]);
  const search = useQuery({
    queryKey: ['patients', session?.tenantId, 'search', q, 'booking'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/patients/search', { params: { query: { q, limit: 10 } } })),
    enabled: q.length > 1 && !disabled,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
  const options: BookingPatient[] = useMemo(
    () =>
      (search.data?.items ?? []).map((item: PatientSearchItem) => ({
        id: item.id,
        displayName: item.displayName,
        fileNumber: item.fileNumber,
      })),
    [search.data],
  );
  return (
    <Autocomplete<BookingPatient, false, false, false>
      value={value}
      onChange={(_event, next) => onChange(next)}
      inputValue={input}
      onInputChange={(_event, next) => setInput(next)}
      options={value && !options.some((option) => option.id === value.id) ? [value, ...options] : options}
      filterOptions={(items) => items}
      getOptionLabel={label}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      noOptionsText={t('sch:booking.patient_none')}
      disabled={disabled}
      clearText={t('sch:booking.patient_clear')}
      sx={{ flex: 2, minWidth: 240 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('sch:booking.patient')}
          placeholder={t('sch:booking.patient_search')}
          required
          inputRef={inputRef}
          inputProps={{ ...params.inputProps, 'data-testid': 'booking-patient' }}
        />
      )}
    />
  );
}

export function BookingDialog({ open, mode, prefill, onClose, onDone }: BookingDialogProps) {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const titleId = useId();
  const channelId = useId();
  const { branchId } = useActiveBranch();
  const tenantId = session?.tenantId;

  const [patient, setPatient] = useState<BookingPatient | null>(null);
  const [channel, setChannel] = useState<AppointmentChannel>('Phone');
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [providerId, setProviderId] = useState<string>('');
  const [stripStart, setStripStart] = useState<string | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [selected, setSelected] = useState<SlotView | null>(null);
  const [wanted, setWanted] = useState<{ start: string; providerId?: string | null; after?: boolean } | null>(null);
  const [notes, setNotes] = useState('');
  const [overrideOverlap, setOverrideOverlap] = useState(false);
  const [ackedRules, setAckedRules] = useState<string[]>([]);
  const [overrideRules, setOverrideRules] = useState(false);
  const [admissionReason, setAdmissionReason] = useState('');
  const [overrideLock, setOverrideLock] = useState(false);
  const lock = useLockPolicy();
  const [overbook, setOverbook] = useState(false);
  // AC-SCH-130.2: کد ارجاع ملی رزرو را روی بلوک ارجاعی می‌نشاند؛ بی‌کد همان زمان PHX-SCH-0332 می‌گیرد.
  const [referralCode, setReferralCode] = useState('');
  const [referralRequired, setReferralRequired] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();
  const [notice, setNotice] = useState<string | null>(null);
  // AP-SCH-02.
  const [roomOverride, setRoomOverride] = useState<string | null>(null);
  const [waive, setWaive] = useState(false);
  // US-SCH-024 (AP-BIL-03): مصرف اعتبار پکیج در ثبت (پیش‌فرض روشن وقتی اعتبار کافی است — C-AGENT-826).
  const [usePackage, setUsePackage] = useState(true);
  const [waiverReason, setWaiverReason] = useState('');
  // US-SCH-127: انتخاب سطح نوبت فقط برای کارکنان.
  const [depositDueMode, setDepositDueMode] = useState<'' | 'AfterBooking' | 'BeforeStart' | 'UntilStart' | 'AtCenter'>('');
  const [depositDueOffsetMinutes, setDepositDueOffsetMinutes] = useState('1440');
  const [onDepositUnpaid, setOnDepositUnpaid] = useState<'' | 'CancelAndRelease' | 'KeepAppointment'>('');
  const [hold, setHold] = useState<HoldView | null>(null);
  const holdRef = useRef<string | null>(null);
  // AP-QA-1A: درخواست نگه‌داشت در جریان؛ ثبت (Ctrl+Enter بلافاصله پس از خلاصه) منتظر آن می‌ماند تا بدون holdId با نگه‌داشت خودش تداخل نکند.
  const holdPendingRef = useRef<Promise<unknown> | null>(null);
  const [recurring, setRecurring] = useState(false);
  const [seriesCount, setSeriesCount] = useState(8);
  const [intervalWeeks, setIntervalWeeks] = useState(1);
  const [seriesPreview, setSeriesPreview] = useState<SeriesPreviewView | null>(null);
  const [scope, setScope] = useState<'this' | 'following'>('this');
  const slotRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const patientInput = useRef<HTMLInputElement>(null);
  const serviceInput = useRef<HTMLInputElement>(null);

  // فوکوس اول: مراجعه‌کننده؛ با مراجعه‌کننده پیش‌انتخاب (ادامه ثبت سریع) خدمت (FLOW-01 ← FLOW-02). پس از انتقال باز شدن دیالوگ
  // و بازگشت فوکوس دیالوگ قبلی.
  useEffect(() => {
    if (!open || mode === 'reschedule') {
      return undefined;
    }
    const target = prefill?.patient && !prefill.serviceId ? serviceInput : prefill?.patient ? null : patientInput;
    const timer = window.setTimeout(() => target?.current?.focus(), 150);
    return () => window.clearTimeout(timer);
  }, [open, mode, prefill]);

  const baseOptions = useBookingOptions(branchId, null, open);
  const providerOptions = useBookingOptions(branchId, serviceId, open && serviceId !== null);
  const today = baseOptions.data?.today ?? null;
  const timeZone = baseOptions.data?.timeZone;
  const serviceSearch = useServiceSearch(branchId, open);
  const services = serviceSearch.services;
  const service: BookingServiceOption | undefined =
    serviceSearch.find(serviceId) ?? (baseOptions.data?.services ?? []).find((item) => item.id === serviceId);
  const providers = (serviceId ? providerOptions.data?.providers : baseOptions.data?.providers) ?? [];
  const canOverride = access.can('sch.appointment.override');
  const canOverbook = access.can('sch.appointment.overbook');
  const canWaitlist = access.can('sch.waitlist.manage');

  // بازنشانی با هر باز شدن.
  useEffect(() => {
    if (!open) {
      return;
    }
    setPatient(prefill?.patient ?? null);
    setChannel(mode === 'walk-in' ? 'InPerson' : 'Phone');
    setServiceId(prefill?.serviceId ?? null);
    setQuantity(prefill?.quantity ?? 1);
    setProviderId(prefill?.providerId ?? '');
    setStripStart(prefill?.date ?? null);
    setDate(prefill?.date ?? null);
    setSelected(null);
    setWanted(prefill?.start ? { start: prefill.start, providerId: prefill.providerId } : null);
    setNotes('');
    setOverrideOverlap(false);
    setOverbook(false);
    setSubmitError(undefined);
    setNotice(null);
    setRoomOverride(null);
    setWaive(false);
    setWaiverReason('');
    setRecurring(false);
    setSeriesCount(8);
    setIntervalWeeks(1);
    setSeriesPreview(null);
    setScope('this');
  }, [open, mode, prefill]);

  // امروز شعبه پس از بارگذاری گزینه‌ها.
  useEffect(() => {
    if (open && today) {
      setStripStart((current) => current ?? today);
      setDate((current) => current ?? today);
    }
  }, [open, today]);

  const effectiveProvider = providerId || null;
  const searchKey = `${branchId}|${serviceId}|${quantity}|${effectiveProvider}|${date}`;
  const slots = useQuery({
    queryKey: schKeys.slots(tenantId, searchKey),
    queryFn: (): Promise<SlotSearchView> =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/availability:search', {
          body: {
            serviceId,
            quantity,
            branchId,
            providerId: effectiveProvider,
            from: date,
            days: 1,
            firstAvailable: false,
          },
        }),
      ),
    enabled: open && mode !== 'walk-in' && serviceId !== null && date !== null && branchId !== null,
    placeholderData: keepPreviousData,
  });
  const slotList = useMemo(() => (slots.isPlaceholderData ? [] : (slots.data?.slots ?? [])), [slots.data, slots.isPlaceholderData]);

  // انتخاب زمان خواسته‌شده (پیش‌پر تقویم، «اولین زمان آزاد» یا زمان بعدی پس از رقابت).
  useEffect(() => {
    if (!wanted || slots.isFetching || slots.isPlaceholderData || !slots.data) {
      return;
    }
    const target = new Date(wanted.start).getTime();
    const candidates = slotList.filter((slot) => !wanted.providerId || slot.providerId === wanted.providerId);
    const match = wanted.after
      ? candidates.find((slot) => new Date(slot.start).getTime() > target)
      : candidates.find((slot) => new Date(slot.start).getTime() === target);
    setWanted(null);
    if (match) {
      setSelected(match);
      const index = slotList.findIndex((slot) => slotKey(slot) === slotKey(match));
      window.setTimeout(() => slotRefs.current[index]?.focus(), 0);
    }
  }, [wanted, slots.isFetching, slots.isPlaceholderData, slots.data, slotList]);

  const grouped = useMemo(() => {
    const groups = new Map<string, { name: string; slots: { slot: SlotView; index: number }[] }>();
    slotList.forEach((slot, index) => {
      const group = groups.get(slot.providerId) ?? { name: slot.providerName ?? '', slots: [] };
      group.slots.push({ slot, index });
      groups.set(slot.providerId, group);
    });
    return [...groups.entries()];
  }, [slotList]);

  const firstAvailable = useMutation({
    mutationFn: (): Promise<SlotSearchView> =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/availability:search', {
          body: {
            serviceId,
            quantity,
            branchId,
            providerId: effectiveProvider,
            from: today,
            days: 14,
            firstAvailable: true,
          },
        }),
      ),
    onSuccess: (result) => {
      const earliest = [...result.slots].sort((a, b) => a.start.localeCompare(b.start))[0] ?? result.nextAvailable;
      if (!earliest) {
        setNotice(t('sch:booking.no_next'));
        return;
      }
      setNotice(null);
      setStripStart(earliest.date);
      setDate(earliest.date);
      setSelected(null);
      setWanted({ start: earliest.start, providerId: earliest.providerId });
    },
  });

  const chosenRoom = roomOverride ?? selected?.roomId ?? null;
  // «استفاده از پکیج» خاموش ← پیش‌نمایش دوباره با usePackage=false تا بیعانه‌ای که ثبت واقعاً می‌گیرد نمایش داده شود (C-AGENT-1055).
  const previewKey = selected && patient ? `${patient.id}|${serviceId}|${quantity}|${slotKey(selected)}|${chosenRoom}|${usePackage}` : '';
  const preview = useQuery({
    queryKey: schKeys.preview(tenantId, previewKey),
    queryFn: (): Promise<BookingPreviewView> =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/appointments:preview', {
          body: {
            patientId: patient?.id ?? null,
            serviceId,
            quantity,
            providerId: selected?.providerId ?? null,
            start: selected?.start ?? null,
            roomId: chosenRoom,
            branchId,
            channel,
            notes: null,
            overridePatientOverlap: false,
            overrideAdmissionRules: false,
            overbook: false,
            waiveDeposit: false,
            usePackage: usePackage ? null : false,
          },
        }),
      ),
    enabled: open && mode !== 'walk-in' && previewKey !== '',
    // فقط تغییر «استفاده از پکیج» پیش‌نمایش قبلی را تا رسیدن پاسخ تازه نگه می‌دارد (نشان پکیج و گزینه آن نپرد)؛ زمان یا خدمت تازه هرگز.
    placeholderData: (previous, previousQuery) => {
      const key = previousQuery?.queryKey[3];
      return typeof key === 'string' && previewKey !== '' && withoutPackageFlag(key) === withoutPackageFlag(previewKey) ? previous : undefined;
    },
    retry: false,
  });
  const warnings = (preview.data?.warnings ?? []).filter(
    (warning) => !(mode === 'reschedule' && warning.appointmentId === prefill?.appointmentId),
  );
  const overlapBlocking = warnings.some((warning) => warning.blocking && warning.code !== 'admission_rule');
  const admissionBlocking = warnings.some((warning) => warning.blocking && warning.code === 'admission_rule');
  const admissionWarnIds = warnings.flatMap((warning) =>
    warning.code === 'admission_rule' && !warning.blocking && warning.ruleId ? [warning.ruleId] : [],
  );
  const admissionAcked = admissionWarnIds.every((id) => ackedRules.includes(id));
  const admissionOverrideOk = !admissionBlocking || (canOverride && overrideRules && admissionReason.trim().length >= 10);
  const rescheduleLocked = mode === 'reschedule' && !!prefill?.status && lock.isLocked(prefill.status);
  const lockOverrideOk = !rescheduleLocked || (lock.canOverride && overrideLock && admissionReason.trim().length >= 10);

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: schKeys.all(tenantId) }),
    [queryClient, tenantId],
  );

  const handleTaken = useCallback(() => {
    // رقابت همزمان: نتایج تازه و انتخاب زمان بعدی (FLOW-02).
    setNotice(t('sch:booking.taken'));
    if (selected) {
      setWanted({ start: selected.start, providerId: effectiveProvider, after: true });
    }
    setSelected(null);
    void queryClient.invalidateQueries({ queryKey: ['scheduling', tenantId, 'slots'] });
  }, [effectiveProvider, queryClient, selected, t, tenantId]);

  const seriesBody = () => {
    const time = selected ? formatTime(selected.start, timeZone) : '';
    return {
      patientId: patient?.id ?? null,
      serviceId,
      quantity,
      providerId: selected?.providerId ?? null,
      roomId: chosenRoom,
      branchId,
      firstDate: selected?.date ?? date,
      time: latinDigits(time) || null,
      weekdays: null,
      intervalWeeks,
      count: seriesCount,
      until: null,
      channel,
    };
  };

  const previewSeries = useMutation({
    mutationFn: (): Promise<SeriesPreviewView> =>
      unwrap(tenantApi.POST('/api/v1/scheduling/appointment-series:preview', { body: { ...seriesBody(), notes: null, overrides: null } })),
    onSuccess: setSeriesPreview,
  });

  const releaseHold = useCallback(() => {
    const held = holdRef.current;
    holdRef.current = null;
    setHold(null);
    if (held) {
      void tenantApi.DELETE('/api/v1/scheduling/holds/{id}', { params: { path: { id: held } } }).catch(() => undefined);
    }
  }, [tenantApi]);

  // US-SCH-014: زمان انتخاب‌شده تا ۱۰ دقیقه برای دیگران آزاد نیست؛ تغییر انتخاب یا بستن دیالوگ نگه‌داشت را رها می‌کند.
  const holdKey = open && mode === 'book' && !recurring && selected && patient && serviceId && branchId ? `${patient.id}|${serviceId}|${slotKey(selected)}|${chosenRoom}` : '';
  useEffect(() => {
    if (!holdKey || !selected || !patient) {
      return undefined;
    }
    let cancelled = false;
    const pending = unwrap(
      tenantApi.POST('/api/v1/scheduling/holds', {
        body: {
          patientId: patient.id,
          serviceId,
          quantity,
          providerId: selected.providerId,
          start: selected.start,
          roomId: chosenRoom,
          branchId,
          channel,
          minutes: null,
        },
        headers: { 'Idempotency-Key': globalThis.crypto.randomUUID() },
      }),
    )
      .then((view) => {
        if (cancelled) {
          void tenantApi.DELETE('/api/v1/scheduling/holds/{id}', { params: { path: { id: view.id } } }).catch(() => undefined);
          return;
        }
        holdRef.current = view.id;
        setHold(view);
      })
      .catch((error: unknown) => {
        if (!cancelled && isApiError(error) && (error.code === 'PHX-SCH-0201' || error.code === 'PHX-SCH-0209')) {
          handleTaken();
        }
      });
    holdPendingRef.current = pending;
    void pending.finally(() => {
      if (holdPendingRef.current === pending) {
        holdPendingRef.current = null;
      }
    });
    return () => {
      cancelled = true;
      if (holdPendingRef.current === pending) {
        holdPendingRef.current = null;
      }
      releaseHold();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdKey]);

  // پیش‌نمایش سری با تغییر زمان پایه کهنه می‌شود.
  useEffect(() => setSeriesPreview(null), [selected?.start, selected?.providerId]);

  // AC-SCH-050.2: زمان آزادی نیست ← ثبت سریع در لیست انتظار همان خدمت و ارائه‌دهنده از روز انتخابی تا دو هفته.
  const joinWaitlist = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/waitlist', {
          body: {
            patientId: patient?.id ?? null,
            serviceId,
            providerId: effectiveProvider,
            branchId,
            dateFrom: date,
            dateTo: date ? addDays(date, 14) : null,
            weekdays: null,
            timeFrom: null,
            timeTo: null,
            priority: 1,
            expiresOn: null,
            notes: notes.trim() || null,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => setNotice(t('sch:booking.waitlist_joined')),
    onError: setSubmitError,
  });

  const submit = useMutation({
    mutationFn: async (): Promise<string> => {
      const headers = { 'Idempotency-Key': idempotency.current() };
      if (mode === 'walk-in') {
        const view = await unwrap(
          tenantApi.POST('/api/v1/scheduling/appointments:walk-in', {
            body: {
              patientId: patient?.id ?? null,
              serviceId,
              quantity,
              providerId: effectiveProvider,
              branchId,
              overbook,
              notes: notes.trim() || null,
              acknowledgedRuleIds: ackedRules,
              overrideAdmissionRules: overrideRules,
              overrideReason: admissionReason.trim() || null,
            },
            headers,
          }),
        );
        return view.id;
      }
      if (mode === 'reschedule' && prefill?.appointmentId) {
        const view = await unwrap(
          tenantApi.POST('/api/v1/scheduling/appointments/{id}:reschedule', {
            params: { path: { id: prefill.appointmentId } },
            body: {
              start: selected?.start ?? null,
              providerId: selected?.providerId ?? null,
              roomId: chosenRoom,
              rowVersion: prefill.rowVersion !== undefined ? Number(prefill.rowVersion) : null,
              scope: prefill.seriesId ? scope : null,
              reason: overrideRules || overrideLock ? admissionReason.trim() || null : null,
              overrideLock,
              acknowledgedRuleIds: ackedRules,
              overrideAdmissionRules: overrideRules,
            },
          }),
        );
        return view.id;
      }
      if (recurring && selected) {
        // US-SCH-022: هر جلسه متداخل با جایگزین پیشنهادی (پیش‌نمایش) ثبت می‌شود.
        const overrides = (seriesPreview?.occurrences ?? [])
          .filter((item) => !item.available && item.alternative)
          .map((item) => ({ index: Number(item.index), start: item.alternative!.start }));
        const series = await unwrap(
          tenantApi.POST('/api/v1/scheduling/appointment-series', {
            body: { ...seriesBody(), notes: notes.trim() || null, overrides },
            headers,
          }),
        );
        return series.appointmentIds[0] ?? '';
      }
      if (holdPendingRef.current) {
        await holdPendingRef.current;
      }
      const heldId = holdRef.current;
      const view = await unwrap(
        tenantApi.POST('/api/v1/scheduling/appointments', {
          body: {
            patientId: patient?.id ?? null,
            serviceId,
            quantity,
            providerId: selected?.providerId ?? null,
            start: selected?.start ?? null,
            roomId: chosenRoom,
            branchId,
            channel,
            notes: notes.trim() || null,
            overridePatientOverlap: overrideOverlap,
            holdId: heldId,
            overbook: false,
            waiveDeposit: waive,
            depositWaiverReason: waive ? waiverReason.trim() || null : null,
            usePackage: preview.data?.package?.sufficient ? usePackage : null,
            acknowledgedRuleIds: ackedRules,
            overrideAdmissionRules: overrideRules,
            overrideReason: admissionReason.trim() || null,
            depositDueMode: depositDueMode || null,
            depositDueOffsetMinutes: depositDueMode === 'BeforeStart' ? Number(depositDueOffsetMinutes) : null,
            onDepositUnpaid: onDepositUnpaid || null,
            referralCode: referralCode.trim() || null,
          },
          headers,
        }),
      );
      // نگه‌داشت به نوبت تبدیل شد؛ رها نشود.
      holdRef.current = null;
      return view.id;
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (appointmentId) => {
      void invalidate();
      onDone({ appointmentId, mode, patientName: patient?.displayName ?? '' });
    },
    onError: (error) => {
      if (isApiError(error) && error.code === 'PHX-SCH-0201' && mode !== 'walk-in') {
        handleTaken();
        return;
      }
      if (isApiError(error) && error.code === 'PHX-SCH-0203' && canOverbook) {
        setNotice(t('sch:booking.overbook_hint'));
      }
      // AC-SCH-130.2: این زمان تنها در بلوک ارجاعی آزاد است — فیلد کد را باز کن، نه پیام «زمان آزاد نیست».
      if (isApiError(error) && error.code === 'PHX-SCH-0332') {
        setReferralRequired(true);
      }
      setSubmitError(error);
    },
  });

  const deposit = mode === 'book' ? (preview.data?.deposit ?? null) : null;
  const alternativeRooms = preview.data?.alternativeRooms ?? [];
  const seriesReady = !recurring || (seriesPreview !== null && seriesPreview.occurrences.every((item) => item.available || item.alternative));
  const ready =
    patient !== null &&
    serviceId !== null &&
    branchId !== null &&
    (!waive || waiverReason.trim().length > 0) &&
    seriesReady &&
    // تا پیش‌نمایش با گزینه پکیج جاری نرسیده، بیعانه نمایش‌داده‌شده ممکن است همان ثبت نباشد.
    (mode !== 'book' || !preview.isPlaceholderData) &&
    (mode === 'walk-in' || (selected !== null && (!overlapBlocking || overrideOverlap))) &&
    admissionAcked &&
    admissionOverrideOk &&
    lockOverrideOk;

  const trySubmit = () => {
    if (recurring && seriesPreview === null && selected !== null && !previewSeries.isPending) {
      previewSeries.mutate();
      return;
    }
    if (!ready || submit.isPending) {
      if (mode !== 'walk-in' && selected === null) {
        setNotice(t('sch:booking.choose_slot'));
      }
      return;
    }
    submit.mutate();
  };

  const onDialogKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      trySubmit();
    }
  };

  // کلیدهای جهت بین زمان‌ها (RTL: چپ ← بعدی).
  const onSlotKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const forward = event.key === 'ArrowLeft' || event.key === 'ArrowDown';
    const backward = event.key === 'ArrowRight' || event.key === 'ArrowUp';
    if (!forward && !backward) {
      return;
    }
    event.preventDefault();
    const next = Math.min(Math.max(index + (forward ? 1 : -1), 0), slotList.length - 1);
    slotRefs.current[next]?.focus();
  };

  const stripDays = stripStart ? Array.from({ length: STRIP_DAYS }, (_, index) => addDays(stripStart, index)) : [];
  const horizonEnd = today && baseOptions.data ? addDays(today, Number(baseOptions.data.horizonDays)) : null;
  const titleKey =
    mode === 'reschedule' ? 'sch:booking.reschedule_title' : mode === 'walk-in' ? 'sch:booking.walk_in_title' : 'sch:booking.title';
  const submitKey =
    mode === 'reschedule' ? 'sch:booking.submit_reschedule' : mode === 'walk-in' ? 'sch:booking.submit_walk_in' : 'sch:booking.submit';
  const selectedProviderName = selected?.providerName ?? '';

  return (
    <Dialog
      open={open}
      onClose={() => !submit.isPending && onClose()}
      aria-labelledby={titleId}
      maxWidth="md"
      fullWidth
      data-screen-id={SCR_STAFF_302}
      onKeyDown={onDialogKeyDown}
    >
      <DialogTitle id={titleId}>{t(titleKey)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {branchId === null && <BranchRequired />}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <PatientPicker
              value={patient}
              onChange={setPatient}
              disabled={mode === 'reschedule'}
              inputRef={patientInput}
            />
            {mode === 'book' && (
              <FormControl>
                <FormLabel id={channelId}>{t('sch:booking.channel')}</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby={channelId}
                  value={channel}
                  onChange={(event) => setChannel(event.target.value as AppointmentChannel)}
                >
                  <FormControlLabel value="Phone" control={<Radio />} label={t('sch:channel.Phone')} />
                  <FormControlLabel value="InPerson" control={<Radio />} label={t('sch:channel.InPerson')} />
                </RadioGroup>
              </FormControl>
            )}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Autocomplete<BookingServiceOption, false, false, false>
              value={service ?? null}
              onChange={(_event, next) => {
                setServiceId(next?.id ?? null);
                setSelected(null);
                setProviderId('');
              }}
              options={services}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options) => options}
              onInputChange={(_event, value, reason) => {
                if (reason === 'input') {
                  serviceSearch.search(value);
                }
              }}
              loading={serviceSearch.isFetching}
              noOptionsText={t('sch:booking.service_none')}
              disabled={mode === 'reschedule'}
              sx={{ flex: 2, minWidth: 220 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('sch:booking.service')}
                  required
                  inputRef={serviceInput}
                  inputProps={{ ...params.inputProps, 'data-testid': 'booking-service' }}
                />
              )}
            />
            {(service?.perUnit || quantity > 1) && (
              <TextField
                label={t('sch:booking.quantity')}
                type="number"
                value={quantity}
                disabled={mode === 'reschedule'}
                onChange={(event) => {
                  const value = Number(latinDigits(event.target.value));
                  setQuantity(Number.isFinite(value) && value >= 1 ? Math.min(value, 100) : 1);
                  setSelected(null);
                }}
                inputProps={{ min: 1, max: 100, 'data-testid': 'booking-quantity' }}
                sx={{ width: 120 }}
              />
            )}
            <TextField
              select
              label={t('sch:booking.provider')}
              value={providerId}
              onChange={(event) => {
                setProviderId(event.target.value);
                setSelected(null);
              }}
              sx={{ flex: 1, minWidth: 180 }}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'booking-provider' }}
            >
              <MenuItem value="">{t('sch:booking.provider_any')}</MenuItem>
              {providers.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {mode === 'walk-in' ? (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {t('sch:booking.walk_in_now')}
              </Typography>
              {canOverbook && (
                <FormControlLabel
                  control={<Checkbox checked={overbook} onChange={(event) => setOverbook(event.target.checked)} />}
                  label={t('sch:booking.overbook')}
                />
              )}
            </Stack>
          ) : (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                <Tooltip title={t('sch:booking.prev_days')}>
                  <span>
                    <IconButton
                      aria-label={t('sch:booking.prev_days')}
                      disabled={!stripStart || !today || stripStart <= today}
                      onClick={() => stripStart && setStripStart(addDays(stripStart, -STRIP_DAYS))}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Stack direction="row" spacing={0.5} role="tablist" aria-label={t('sch:booking.days')} sx={{ flexWrap: 'wrap' }}>
                  {stripDays.map((day) => (
                    <Button
                      key={day}
                      role="tab"
                      aria-selected={day === date}
                      size="small"
                      variant={day === date ? 'contained' : 'text'}
                      disabled={(today !== null && day < today) || (horizonEnd !== null && day > horizonEnd)}
                      onClick={() => {
                        setDate(day);
                        setSelected(null);
                      }}
                      data-testid={`booking-day-${day}`}
                    >
                      {formatDay(day, 'EEEE d')}
                    </Button>
                  ))}
                </Stack>
                <Tooltip title={t('sch:booking.next_days')}>
                  <span>
                    <IconButton
                      aria-label={t('sch:booking.next_days')}
                      disabled={!stripStart}
                      onClick={() => stripStart && setStripStart(addDays(stripStart, STRIP_DAYS))}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="outlined"
                  startIcon={<BoltIcon />}
                  disabled={serviceId === null || firstAvailable.isPending}
                  onClick={() => firstAvailable.mutate()}
                  data-testid="booking-first-available"
                >
                  {t('sch:booking.first_available')}
                </Button>
              </Stack>
              <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
                {t('sch:booking.slots')}
              </Typography>
              {serviceId === null ? (
                <Typography variant="body2" color="text.secondary">
                  {t('sch:booking.select_service')}
                </Typography>
              ) : slots.error ? (
                <ErrorState error={slots.error} compact />
              ) : slotList.length === 0 && !slots.isFetching ? (
                <Stack spacing={1} data-testid="booking-no-slots">
                  <Typography variant="body2" color="text.secondary">
                    {t('sch:booking.no_slots')}
                  </Typography>
                  {slots.data?.nextAvailable ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">
                        {t('sch:booking.next_available', {
                          date: formatDay(slots.data.nextAvailable.date),
                          time: formatTime(slots.data.nextAvailable.start, timeZone),
                        })}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => {
                          const next = slots.data?.nextAvailable;
                          if (next) {
                            setStripStart(next.date);
                            setDate(next.date);
                            setWanted({ start: next.start, providerId: next.providerId });
                          }
                        }}
                      >
                        {t('sch:booking.go_next')}
                      </Button>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('sch:booking.no_next')}
                    </Typography>
                  )}
                  {canWaitlist && mode === 'book' ? (
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ alignSelf: 'flex-start' }}
                      disabled={patient === null || joinWaitlist.isPending || joinWaitlist.isSuccess}
                      onClick={() => joinWaitlist.mutate()}
                      data-testid="booking-waitlist"
                    >
                      {t('sch:booking.waitlist_join')}
                    </Button>
                  ) : null}
                </Stack>
              ) : (
                <Stack spacing={1} data-testid="booking-slots" aria-describedby={`${titleId}-slots-hint`}>
                  {grouped.map(([groupProvider, group]) => (
                    <Stack key={groupProvider} direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 500 }}>
                        {group.name}:
                      </Typography>
                      {group.slots.map(({ slot, index }) => {
                        const active = selected !== null && slotKey(selected) === slotKey(slot);
                        return (
                          <Button
                            key={slotKey(slot)}
                            ref={(element) => {
                              slotRefs.current[index] = element;
                            }}
                            size="small"
                            variant={active ? 'contained' : 'outlined'}
                            aria-pressed={active}
                            onKeyDown={(event) => onSlotKeyDown(event, index)}
                            onClick={() => {
                              setSelected(slot);
                              setNotice(null);
                            }}
                            data-testid="booking-slot"
                            data-start={slot.start}
                          >
                            {formatTime(slot.start, timeZone)}
                          </Button>
                        );
                      })}
                    </Stack>
                  ))}
                  <Typography id={`${titleId}-slots-hint`} variant="caption" color="text.secondary">
                    {t('sch:booking.slots_hint')}
                  </Typography>
                </Stack>
              )}
            </Paper>
          )}

          {notice && (
            <Alert severity="info" role="status" data-testid="booking-notice">
              {notice}
            </Alert>
          )}

          {mode !== 'walk-in' && selected && (
            <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="booking-summary">
              <Typography variant="subtitle2" component="h3">
                {t('sch:booking.summary')}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} alignItems="center">
                <Typography variant="body2">
                  {t('sch:booking.summary_line', {
                    date: formatDay(selected.date, 'EEEE d MMMM'),
                    start: formatTime(preview.data?.start ?? selected.start, timeZone),
                    end: formatTime(preview.data?.end ?? selected.end, timeZone),
                    provider: selectedProviderName,
                  })}
                </Typography>
                <Typography variant="body2">
                  ·{' '}
                  {(preview.data?.roomName ?? selected.roomName)
                    ? t('sch:booking.room', { room: preview.data?.roomName ?? selected.roomName })
                    : t('sch:booking.no_room')}
                </Typography>
                <Typography variant="body2" component="span">
                  · <PriceText price={preview.data?.price ?? selected.price} />
                </Typography>
              </Stack>
              {hold ? (
                <Typography variant="caption" color="text.secondary" data-testid="booking-hold">
                  {t('sch:booking.held_until', { time: formatTime(hold.expiresAt, timeZone) })}
                </Typography>
              ) : null}
              {alternativeRooms.length > 1 ? (
                <TextField
                  select
                  size="small"
                  label={t('sch:booking.change_room')}
                  value={chosenRoom ?? ''}
                  onChange={(event) => setRoomOverride(event.target.value || null)}
                  sx={{ mt: 1, minWidth: 200 }}
                  inputProps={{ 'data-testid': 'booking-room' }}
                >
                  {alternativeRooms.map((room) => (
                    <MenuItem key={room.id} value={room.id}>
                      {room.name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              {preview.data?.package ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }} data-testid="booking-package" data-sufficient={String(preview.data.package.sufficient)}>
                  <Chip
                    size="small"
                    color={preview.data.package.sufficient ? 'success' : 'warning'}
                    label={t('sch:booking.package_badge', {
                      name: preview.data.package.packageName,
                      remaining: faDigits(Number(preview.data.package.remainingSessions)),
                    })}
                  />
                  {preview.data.package.sufficient ? (
                    <FormControlLabel
                      control={<Checkbox checked={usePackage} onChange={(event) => setUsePackage(event.target.checked)} />}
                      label={t('sch:booking.use_package')}
                      data-testid="booking-use-package"
                    />
                  ) : null}
                </Stack>
              ) : null}
              {deposit ? (
                <Stack spacing={1} sx={{ mt: 1 }} data-testid="booking-deposit">
                  {deposit.payAtCenter ? (
                    <Alert severity="info" data-testid="booking-pay-at-center">
                      {t('sch:booking.pay_at_center')}
                    </Alert>
                  ) : (
                    <>
                      <Typography variant="body2" component="div">
                        {t('sch:booking.deposit_line', { due: `${formatDay(deposit.dueAt.slice(0, 10), 'd MMMM')} ${formatTime(deposit.dueAt, timeZone)}` })}{' '}
                        <Money amountMinor={Number(deposit.amount.amount)} currency={deposit.amount.currency} />
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {deposit.dueToRestriction ? t('sch:booking.deposit_restricted') : t('sch:booking.deposit_hint')}
                      </Typography>
                    </>
                  )}
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }} data-testid="booking-deposit-choice">
                    <TextField
                      select
                      size="small"
                      label={t('sch:booking.deposit_due_mode')}
                      value={depositDueMode}
                      onChange={(event) => setDepositDueMode(event.target.value as typeof depositDueMode)}
                      sx={{ minWidth: 180 }}
                      SelectProps={{ displayEmpty: true }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ 'data-testid': 'booking-deposit-due-mode' }}
                    >
                      <MenuItem value="">{t('sch:booking.deposit_choice_inherit')}</MenuItem>
                      <MenuItem value="AfterBooking">{t('sch:policies.deposit_due_mode.AfterBooking')}</MenuItem>
                      <MenuItem value="BeforeStart">{t('sch:policies.deposit_due_mode.BeforeStart')}</MenuItem>
                      <MenuItem value="UntilStart">{t('sch:policies.deposit_due_mode.UntilStart')}</MenuItem>
                      <MenuItem value="AtCenter">{t('sch:policies.deposit_due_mode.AtCenter')}</MenuItem>
                    </TextField>
                    {depositDueMode === 'BeforeStart' ? (
                      <TextField
                        select
                        size="small"
                        label={t('sch:policies.field.depositDueOffsetMinutes')}
                        value={depositDueOffsetMinutes}
                        onChange={(event) => setDepositDueOffsetMinutes(event.target.value)}
                        sx={{ minWidth: 140 }}
                        inputProps={{ 'data-testid': 'booking-deposit-offset' }}
                      >
                        {(['180', '1440', '2880', '4320', '10080', '20160'] as const).map((minutes) => (
                          <MenuItem key={minutes} value={minutes}>
                            {t(`sch:policies.before_start_offset.${minutes}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : null}
                    <TextField
                      select
                      size="small"
                      label={t('sch:booking.on_deposit_unpaid')}
                      value={onDepositUnpaid}
                      onChange={(event) => setOnDepositUnpaid(event.target.value as typeof onDepositUnpaid)}
                      sx={{ minWidth: 180 }}
                      SelectProps={{ displayEmpty: true }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ 'data-testid': 'booking-on-deposit-unpaid' }}
                    >
                      <MenuItem value="">{t('sch:booking.deposit_choice_inherit')}</MenuItem>
                      <MenuItem value="CancelAndRelease">{t('sch:policies.on_deposit_unpaid.CancelAndRelease')}</MenuItem>
                      <MenuItem value="KeepAppointment">{t('sch:policies.on_deposit_unpaid.KeepAppointment')}</MenuItem>
                    </TextField>
                  </Stack>
                  {canOverride && !deposit.payAtCenter ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <FormControlLabel
                        control={<Checkbox checked={waive} onChange={(event) => setWaive(event.target.checked)} />}
                        label={t('sch:booking.waive_deposit')}
                      />
                      {waive ? (
                        <TextField
                          size="small"
                          required
                          label={t('sch:booking.waiver_reason')}
                          value={waiverReason}
                          onChange={(event) => setWaiverReason(event.target.value)}
                          inputProps={{ maxLength: 500, 'data-testid': 'booking-waiver-reason' }}
                          sx={{ flex: 1, minWidth: 220 }}
                        />
                      ) : null}
                    </Stack>
                  ) : null}
                </Stack>
              ) : null}
            </Paper>
          )}

          {mode === 'book' && selected && (
            <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="booking-recurring">
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={recurring}
                      onChange={(event) => {
                        setRecurring(event.target.checked);
                        setSeriesPreview(null);
                      }}
                      inputProps={{ 'data-testid': 'booking-recurring-toggle' } as Record<string, string>}
                    />
                  }
                  label={t('sch:booking.recurring')}
                />
                {recurring ? (
                  <>
                    <TextField
                      size="small"
                      type="number"
                      label={t('sch:booking.series_count')}
                      value={seriesCount}
                      onChange={(event) => {
                        const value = Number(latinDigits(event.target.value));
                        setSeriesCount(Number.isFinite(value) ? Math.min(Math.max(value, 2), 52) : 2);
                        setSeriesPreview(null);
                      }}
                      inputProps={{ min: 2, max: 52, 'data-testid': 'booking-series-count' }}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      select
                      size="small"
                      label={t('sch:booking.series_interval')}
                      value={intervalWeeks}
                      onChange={(event) => {
                        setIntervalWeeks(Number(event.target.value));
                        setSeriesPreview(null);
                      }}
                      sx={{ width: 160 }}
                    >
                      {[1, 2, 4].map((weeks) => (
                        <MenuItem key={weeks} value={weeks}>
                          {t('sch:booking.every_weeks', { n: faDigits(weeks) })}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button size="small" onClick={() => previewSeries.mutate()} disabled={previewSeries.isPending} data-testid="booking-series-preview">
                      {t('sch:booking.series_preview')}
                    </Button>
                  </>
                ) : null}
              </Stack>
              {recurring && seriesPreview ? (
                <Stack component="ul" sx={{ m: 0, pl: 2 }} data-testid="booking-series-occurrences">
                  {seriesPreview.occurrences.map((item) => (
                    <li key={String(item.index)} data-available={item.available ? 'true' : 'false'}>
                      {formatDay(item.date, 'EEEE d MMMM')} · {formatTime(item.start, timeZone)}
                      {item.available
                        ? ''
                        : item.alternative
                          ? ` — ${t('sch:booking.series_alternative', { time: `${formatDay(item.alternative.date, 'd MMMM')} ${formatTime(item.alternative.start, timeZone)}` })}`
                          : ` — ${t('sch:booking.series_no_alternative')}`}
                    </li>
                  ))}
                </Stack>
              ) : null}
              {recurring && seriesPreview && Number(seriesPreview.conflictCount) > 0 ? (
                <Alert severity="warning" sx={{ mt: 1 }} data-testid="booking-series-conflicts">
                  {t('sch:booking.series_conflicts', { n: faDigits(Number(seriesPreview.conflictCount)) })}
                </Alert>
              ) : null}
              {previewSeries.error ? <ErrorState error={previewSeries.error} compact /> : null}
            </Paper>
          )}

          {mode === 'reschedule' && prefill?.seriesId ? (
            <FormControl>
              <FormLabel id={`${titleId}-scope`}>{t('sch:booking.series_scope')}</FormLabel>
              <RadioGroup row aria-labelledby={`${titleId}-scope`} value={scope} onChange={(event) => setScope(event.target.value as 'this' | 'following')}>
                <FormControlLabel value="this" control={<Radio />} label={t('sch:booking.scope_this')} />
                <FormControlLabel value="following" control={<Radio />} label={t('sch:booking.scope_following')} />
              </RadioGroup>
            </FormControl>
          ) : null}

          {warnings.length > 0 && (
            <Alert severity="warning" data-testid="booking-warnings">
              <Typography variant="subtitle2" component="h3">
                {t('sch:booking.warnings')}
              </Typography>
              <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                {warnings.map((warning) => (
                  <li key={`${warning.code}-${warning.ruleId ?? warning.appointmentId ?? ''}`} data-warning={warning.code}>
                    {warning.code === 'admission_rule' && warning.message ? warning.message : t(`sch:booking.warning_${warning.code}`)}
                  </li>
                ))}
              </Stack>
              {admissionWarnIds.map((id) => (
                <FormControlLabel
                  key={id}
                  data-testid="admission-ack"
                  control={
                    <Checkbox
                      checked={ackedRules.includes(id)}
                      onChange={(event) =>
                        setAckedRules(event.target.checked ? [...ackedRules, id] : ackedRules.filter((item) => item !== id))
                      }
                    />
                  }
                  label={t('sch:booking.acknowledge_rule')}
                />
              ))}
              {overlapBlocking && canOverride && (
                <FormControlLabel
                  control={<Checkbox checked={overrideOverlap} onChange={(event) => setOverrideOverlap(event.target.checked)} />}
                  label={t('sch:booking.override_overlap')}
                />
              )}
              {admissionBlocking && canOverride && (
                <Stack spacing={1} data-testid="admission-override">
                  <FormControlLabel
                    control={<Checkbox checked={overrideRules} onChange={(event) => setOverrideRules(event.target.checked)} />}
                    label={t('sch:booking.override_admission')}
                  />
                  <TextField
                    label={t('sch:booking.override_reason')}
                    value={admissionReason}
                    onChange={(event) => setAdmissionReason(event.target.value)}
                    inputProps={{ 'data-testid': 'admission-override-reason', minLength: 10 }}
                  />
                </Stack>
              )}
              {rescheduleLocked && lock.canOverride ? (
                <Stack spacing={1} data-testid="lock-override">
                  <Alert severity="info">{t('sch:booking.locked_hint')}</Alert>
                  <FormControlLabel
                    data-testid="lock-override-toggle"
                    control={<Checkbox checked={overrideLock} onChange={(event) => setOverrideLock(event.target.checked)} />}
                    label={t('sch:booking.override_lock')}
                  />
                  {!(admissionBlocking && canOverride) ? (
                    <TextField
                      label={t('sch:booking.override_reason')}
                      value={admissionReason}
                      onChange={(event) => setAdmissionReason(event.target.value)}
                      inputProps={{ 'data-testid': 'lock-override-reason', minLength: 10 }}
                    />
                  ) : null}
                </Stack>
              ) : null}
            </Alert>
          )}

          {mode === 'book' && (referralRequired || referralCode.length > 0) && (
            <TextField
              label={t('sch:booking.referral_code')}
              value={referralCode}
              onChange={(event) => setReferralCode(event.target.value)}
              required={referralRequired}
              helperText={t('sch:booking.referral_code_hint')}
              inputProps={{ maxLength: 60, 'data-testid': 'booking-referral-code' }}
            />
          )}

          {mode === 'book' && !referralRequired && referralCode.length === 0 && (
            <Button
              size="small"
              onClick={() => setReferralRequired(true)}
              data-testid="booking-referral-open"
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('sch:booking.referral_code_add')}
            </Button>
          )}

          {mode !== 'reschedule' && (
            <TextField
              label={t('sch:booking.notes')}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              inputProps={{ maxLength: 500 }}
            />
          )}

          {submitError ? <ErrorState error={submitError} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submit.isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={trySubmit}
          disabled={(!ready && !(recurring && seriesPreview === null && selected !== null)) || submit.isPending || previewSeries.isPending}
          data-testid="booking-submit"
          aria-keyshortcuts="Control+Enter"
        >
          {recurring && seriesPreview === null ? t('sch:booking.series_preview') : t(submitKey)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
